import React, { useEffect, useMemo } from 'react';
import { useIsFetching, useMutation } from '@tanstack/react-query';
import useOrderPackage from '../../../hooks/useOrderPackage';
import useOption from '../../../hooks/useOption';
import useBackgrounds from '../../../hooks/useBackgrounds';
import usePoses from '../../../hooks/usePoses';
import { addonSheetToSheet } from '../MultiPose/functions';
import Builder from '../MultiPose/Builder';
import { AddonSheet } from '../../../models';
import useAddons from '../../../hooks/useAddons';
import usePackagePoses from '../../../hooks/usePackagePoses';
import { sheetUnitPrice } from '../../../utils/functions';

export default function AddonSheetBuilder({ isElementary = true, onSave, orderPackageId, sheetId }) {
  // Data
  const { data: orderPackage } = useOrderPackage(orderPackageId);
  const { data: option } = useOption(orderPackage && orderPackage.optionId, orderPackage && orderPackage.schoolId);
  const { data: backgrounds } = useBackgrounds(orderPackage && orderPackage.packageId);
  const { data: poses } = usePoses(orderPackage && orderPackage.studentId, orderPackage && orderPackage.packageId);
  const { data: elementaryAddons } = useAddons(true);
  const { data: seniorAddons } = useAddons(false);

  const addons = [...elementaryAddons, ...seniorAddons];

  const { data: packagePoses } = usePackagePoses(orderPackage?.packageId);

  const {
    mutate,
    isLoading,
  } = useMutation({
    mutationFn: ({ sheets }) => {
      let prevIndex = 0;

      // Destroy any sheets on the orderPackage (if any), and replace with the new sheets.
      //
      // The offer's free magnet sheet needs NO protection here, unlike in
      // IndividualPrintsBuilder: this builder rebuilds `imageTypes` from
      // `orderPackage.addonSheets` (below), so every destroyed sheet is
      // re-created from the same list and the free one round-trips. Exempting
      // it from the wipe would keep the old row AND add a new one -- two $0
      // sheets, four magnets printed.
      const oldSheets = orderPackage.addonSheets.map((sheet) => {
        if (!sheetId) sheet.isMarkedForDestruction = true;

        if (sheetId && sheet.id.toString() === sheetId.toString()) {
          prevIndex = sheet.index;
          sheet.isMarkedForDestruction = true;
        }

        return sheet;
      });

      const newSheets = sheets.map((sheetParams) => {
        const addon = addons.find((cur) => cur.id.toString() === sheetParams.imageType.id.toString());

        if (sheetId) return new AddonSheet({ ...sheetParams, addon, addonId: addon.id, index: prevIndex });

        return new AddonSheet({ ...sheetParams, addon, addonId: addon.id });
      });

      orderPackage.addonSheets = [...oldSheets, ...newSheets];

      return orderPackage.save({ with: 'addonSheets' });
    },
    onSuccess: () => {
      if (Object.keys(orderPackage.errors).length > 0) return;

      onSave();
    },
  });

  // Calculate subtotal to present during review
  const subtotal = useMemo(() => {
    if (!orderPackage) return 0;
    if (!option) return 0;

    // sheetUnitPrice: server-stamped snapshot, shared fallback rule (keyed on
    // the ORDER PACKAGE price, like the charge — the old inline rule keyed on
    // the option price and could disagree with checkout).
    return option.prices[0].price + orderPackage.addonSheets.reduce((acc, cur) => (
      acc + sheetUnitPrice(cur, orderPackage)
    ), 0);
  }, [!!orderPackage, !!option]);

  // UI Hooks
  const isFetching = useIsFetching();

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  if (!orderPackage) return null;
  if (isFetching) return null;

  const editableSheets = orderPackage
    .addonSheets
    .filter((sheet) => {
      if (sheetId) return sheet?.id?.toString() === sheetId.toString();

      return sheet;
    });

  return (
    <Builder
      isEditingSingleSheet={!!sheetId}
      backgrounds={backgrounds}
      packagePoses={packagePoses}
      imageTypes={orderPackage.addonSheets.map((addonSheet) => ({ name: addonSheet.addon.name, id: addonSheet.addon.id }))}
      initialSheets={editableSheets.map((addonSheet) => addonSheetToSheet(addonSheet))}
      isLoading={isLoading}
      option={{ ...option, poses: Number.POSITIVE_INFINITY, prices: [{ price: subtotal }] }}
      packageId={orderPackage.packageId}
      poses={poses}
      onConfirm={(sheets) => mutate({ sheets })}
    />
  );
}
